import { t } from "i18next";

import React, { useState } from "react";

import { FireIcon } from "@components/OrderTaking/Components/Icons/FireIcon";

import { FLAME_COLOR } from "./tokens";
import type { OrderEnergy } from "./useOrderEnergy";

// Module-level counter — only used as a fallback seed for the lazy initializer
// below so each instance keeps a stable id across re-renders. Reading it
// inside `useState(() => ...)` is called once per mount (React 17 has no
// useId), so StrictMode's double render does not increment it twice.
// TODO: migrate to React.useId() when React 18 is adopted.
let bannerSeq = 0;

type OrderEnergyBannerViewProps = {
    floating?: boolean;
    tall?: boolean;
    energy: OrderEnergy;
};

/** Pure presentational view — no hook, no Redux subscription. */
export function OrderEnergyBannerView({
    floating = false,
    tall = false,
    energy,
}: OrderEnergyBannerViewProps): JSX.Element | null {
    const [footnoteId] = useState(
        () => `order-energy-banner-footnote-${++bannerSeq}`
    );
    if (!energy.visible) return null;

    // No <abbr title=…> here: kiosks are touch-only and never trigger
    // hover/focus tooltips, so "RI" alone is unintelligible (WCAG 3.1.4).
    // The full term is rendered inline; the asterisk + footnote remain for
    // the regulatory reference (EU 1169/2011).
    return (
        <section
            aria-describedby={footnoteId}
            className="order-energy-banner"
            data-floating={floating ? "true" : undefined}
            data-tall={tall ? "true" : undefined}
        >
            <span aria-hidden className="order-energy-banner__accent" />
            <div className="order-energy-banner__main">
                <span aria-hidden className="order-energy-banner__icon-wrap">
                    <span className="order-energy-banner__icon">
                        <FireIcon
                            color={FLAME_COLOR}
                            width="100%"
                            height="100%"
                        />
                    </span>
                </span>
                <div className="order-energy-banner__text">
                    <h2 className="order-energy-banner__title">
                        {t("Energy intake of the order")}
                    </h2>
                    <span
                        className="order-energy-banner__value"
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        <strong className="order-energy-banner__value-strong">
                            {energy.kcalText}
                        </strong>
                        {"  ·  ≈ "}
                        {energy.ri}
                        {"% "}
                        {t("of Reference Intake")}*
                    </span>
                </div>
            </div>
            <span id={footnoteId} className="order-energy-banner__footnote">
                {t("Reference Intakes for an average adult (2,000 kcal)")}
            </span>
        </section>
    );
}
